// src/modules/hr/pages/EmployeeDocuments.jsx — Phase 20
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Tag, Modal, Form, DatePicker, Input, message, Space, Popconfirm,
} from 'antd';
import { FileProtectOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchEmployees, fetchDocuments, createDocument, deleteDocument } from '../api';
import { DOCUMENT_TYPE_OPTIONS } from '../constants/hrConstants';

const { Title, Text } = Typography;

export default function EmployeeDocuments() {
  const { can } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (employeeId) params.employeeId = employeeId;
      const res = await fetchDocuments(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load documents'); }
    finally { setLoading(false); }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await createDocument({ ...v, issueDate: v.issueDate?.toISOString(), expiryDate: v.expiryDate?.toISOString() });
      message.success('Document added'); setAddOpen(false); form.resetFields(); load();
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
    { title: 'Type', dataIndex: 'docType', width: 180, render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
    { title: 'Title', dataIndex: 'title', render: (v) => v || '—' },
    { title: 'Issue', dataIndex: 'issueDate', width: 110, render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
    { title: 'Expiry', dataIndex: 'expiryDate', width: 110, render: (v) => v ? <Tag color={dayjs(v).isBefore(dayjs()) ? 'red' : 'default'}>{dayjs(v).format('DD/MM/YYYY')}</Tag> : '—' },
    { title: 'File', dataIndex: 'fileUrl', width: 80, render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '—' },
    { title: '', key: 'a', width: 50, render: (_, r) => can('hrDocuments', 'delete') && <Popconfirm title="Remove document?" onConfirm={() => deleteDocument(r._id).then(() => { message.success('Removed'); load(); })}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm> },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><FileProtectOutlined style={{ marginRight: 8 }} />Employee Documents</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Document vault (files stored on ImageKit)</Text>
        </Col>
        <Col>
          <Space>
            <Select allowClear showSearch optionFilterProp="children" style={{ width: 240 }} placeholder="Filter by employee" value={employeeId} onChange={(v) => setEmployeeId(v || null)}>{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select>
            <Button icon={<ReloadOutlined />} onClick={load} />
            {can('hrDocuments', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add</Button>}
          </Space>
        </Col>
      </Row>

      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 900 }} pagination={{ pageSize: 50 }} />

      <Modal title="Add Document" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="employee" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Form.Item>
          <Form.Item name="docType" label="Document Type" rules={[{ required: true }]}><Select options={DOCUMENT_TYPE_OPTIONS} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="title" label="Title"><Input /></Form.Item>
          <Form.Item name="fileUrl" label="File URL" rules={[{ required: true }]}><Input placeholder="Upload via the file uploader, then paste URL" /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="issueDate" label="Issue Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="expiryDate" label="Expiry Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
